import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { LayoutComponent } from '../LayoutComponent';
import { Flex, Content, View, Link, ProgressCircle, DialogContainer } from '@adobe/react-spectrum';
import ResponsibleUsersSearch from './component/ResponsibleUsersSearch';
import { useDependency } from '../../../../contexts/DependencyProvider';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../state/store';
import { ToastQueue } from '@react-spectrum/toast';
import useComonentReload from '../../../../hooks/UseComponentReload';
import ContactCard from './component/ContactCard';
import { FPAData, FPADataTypes } from '../../../../infra/protected/FPA/FPAData';
import { ListActivityUserRequest } from '../../../../services/soap/team/requests/ListActivityUserRequest';
import { UpdateActivityUserRequest } from '../../../../services/soap/team/requests/UpdateActivityUserRequest';
import { UpdateProjectUserRequest } from '../../../../services/soap/team/requests/UpdateProjectUserRequest';
import { UpdateFolderUserRequest } from '../../../../services/soap/team/requests/UpdateFolderUserRequest';
import { ListProjectUserRequest } from '../../../../services/soap/team/requests/ListProjectUserRequest';
import { ListFolderUserRequest } from '../../../../services/soap/team/requests/ListFolderUserRequest';
import { GetFolderProjectTypeRequest } from '../../../../services/soap/project/requests/GetFolderProjectTypeRequest';
import { t } from 'i18next';
import styles from './ResponsibleUsers.module.css';


interface ResponsibleUsersProps {
  selectedItem?: FPAData;
}

type UpdateRequestFunction = () => Promise<{ result: string }>;

function ResponsibleUsers({ selectedItem }: ResponsibleUsersProps) {
  const [reloadComponent] = useComonentReload();
  const { projectService,teamService, store } = useDependency();
  const [responsibleUsers, setResponsibleUsers] = useState<any>([]);
  const [showLoader, setShowLoader] = useState<boolean>(true);
  const [isOpenSearchContact, setIsOpenSearchContact] = useState<boolean>(false);
  const [selectedRoleId, setSelectedRoleId] = useState<string>('');
  const [rolesLoaded, setRolesLoaded] = useState<boolean>(false);
  const [roleDataVersion, setRoleDataVersion] = useState<number>(0); // Force re-render when roles change
  const roleData = useRef<any[]>([])
  const current_Item = useRef<any>([]);

//   {
//     "id": "563506101",
//     "folderProjectTypeId": "449310101",
//     "accessRoleId": "518140101",
//     "accessRoleName": "KAM",
//     "type": "0",
//     "priority": "2"
// }

  // Group users by role when roleData exists - using useMemo for optimization
  const groupedUsers = useMemo(() => {
    if (!roleData.current || roleData.current.length === 0) {
      return null; // Use existing functionality
    }

    const groupedUsers: any = {};
    
    // Initialize groups for each role using accessRoleId
    roleData.current.forEach((role: any) => {
      const roleId = role.accessRoleId || role.id || role.roleId;
      if (roleId) {
        groupedUsers[roleId] = {
          roleInfo: role,
          users: []
        };
      }
    });

    // Group existing users by their role ID - check multiple possible field names
    responsibleUsers?.forEach((user: any) => {
      const userRoleId = user.accessRoleId || user.roleId || user.role_id || user.role || user.accessRoleId;
      
      // Try to find matching role in groupedUsers
      let matchingRoleId = null;
      if (userRoleId && groupedUsers[userRoleId]) {
        matchingRoleId = userRoleId;
      } else {
        // If direct match fails, try to find by role name or other identifiers
        const userRoleName = user.roleName || user.accessRoleName;
        if (userRoleName) {
          matchingRoleId = Object.keys(groupedUsers).find(roleId => {
            const role = groupedUsers[roleId].roleInfo;
            return role.accessRoleName === userRoleName || role.name === userRoleName || role.roleName === userRoleName;
          });
        }
      }
      
      if (matchingRoleId) {
        groupedUsers[matchingRoleId].users.push(user);
      } else {
        // If user doesn't have a matching role, add to first available role
        const firstRoleId = Object.keys(groupedUsers)[0];
        if (firstRoleId) {
          groupedUsers[firstRoleId].users.push(user);
        }
      }
    });

    return groupedUsers;
  }, [responsibleUsers, rolesLoaded, roleDataVersion]);

  useEffect(() => {
    (async () => {
      setShowLoader(true);
      setRolesLoaded(false);
      
      // Load both data sources
      await Promise.all([
        loadResponsibleUsers(),
        getActivityTypeRoles()
      ]);
      
      setRolesLoaded(true);
      setShowLoader(false);
    })();
  }, [selectedItem, reloadComponent]);

  
    const getActivityTypeRoles = useCallback(async () => {
      try {
            let res = await projectService.getFolderProjectType(new GetFolderProjectTypeRequest(store.Server, store.SessionId, selectedItem?.item_type_id));
            const roles = res?.TYPE[0]?.ROLES?.ROLE || res?.TYPE[0]?.ROLES;
            const processedRoles = roles ? (Array.isArray(roles) ? roles : [roles]) : [];
            roleData.current = processedRoles;
            setRoleDataVersion(prev => prev + 1); // Trigger re-render
            //console.log('Loaded roles:', processedRoles);
      } catch (error) {
        console.error('Error loading roles:', error);
        roleData.current = [];
        setRoleDataVersion(prev => prev + 1);
      }
    }, [selectedItem]);

  const loadResponsibleUsers = async () => {
    if (!selectedItem) return;

    try {
      switch (selectedItem.type) {
        case FPADataTypes.ACTIVITY:
          let listActivityUser = await teamService.listActivityUser(new ListActivityUserRequest(store.Server, store.SessionId, Number(selectedItem.id)));
          const activityUsers = listActivityUser?.ROW || [];
          const processedActivityUsers = Array.isArray(activityUsers) ? activityUsers : [activityUsers].filter(Boolean);
          setResponsibleUsers(processedActivityUsers);
          current_Item.current = listActivityUser;
          //console.log('Loaded activity users:', processedActivityUsers);
          break;
        case FPADataTypes.PROJECT:
          let listProjectUser = await teamService.listProjectUser(new ListProjectUserRequest(store.Server, store.SessionId, Number(selectedItem.id)));
          const projectUsers = listProjectUser?.ROWS?.ROW || [];
          const processedProjectUsers = Array.isArray(projectUsers) ? projectUsers : [projectUsers].filter(Boolean);
          setResponsibleUsers(processedProjectUsers);
          current_Item.current = listProjectUser;
          //console.log('Loaded project users:', processedProjectUsers);
          break;
        case FPADataTypes.FOLDER:
          let listFolderUser = await teamService.listFolderUser(new ListFolderUserRequest(store.Server, store.SessionId, Number(selectedItem.id)));
          const folderUsers = listFolderUser?.ROWS?.ROW || [];
          const processedFolderUsers = Array.isArray(folderUsers) ? folderUsers : [folderUsers].filter(Boolean);
          setResponsibleUsers(processedFolderUsers);
          current_Item.current = listFolderUser;
          //console.log('Loaded folder users:', processedFolderUsers);
          break;
      }
    } catch (error) {
      console.error('Error loading responsible users:', error);
      setResponsibleUsers([]);
    }
  };

  const updateItem = async (updateRequest: UpdateRequestFunction, successMessage: string) => {
    const updatedItem = await updateRequest();
    if (updatedItem.result === 'OK') {
      ToastQueue.positive(successMessage, { timeout: 3000 });
    }
  };

  const updateContactNote = (contactId: string, description: string) => {
    let contactRec: any = {};

    switch (selectedItem?.type) {
      case FPADataTypes.ACTIVITY:
        contactRec = current_Item.current?.ROW?.find((contact: any) => contact.id == contactId);
        contactRec.note = description;
        //console.log('contactRec', contactRec);
        break;
      case FPADataTypes.PROJECT:
      case FPADataTypes.FOLDER:
        contactRec = current_Item.current?.ROWS.ROW?.find((contact: any) => contact.id == contactId);
        contactRec.note = description;
        //console.log('contactRec', contactRec);
        break;
    }
    //console.log('current_Item.current', current_Item.current);
    return contactRec || {};
  };

  const onSaveDescription = async (contactId: string, description: string) => {
    if (!selectedItem) return;

    let updateContact = updateContactNote(contactId, description);

    const requestData = {
      server: store.Server,
      sessionId: store.SessionId,
      data: {
        ...updateContact,
      },
    };

    let successMessage = t('note_saved_successfully', { ns: 'layout_components' });

    switch (selectedItem.type) {
      case FPADataTypes.ACTIVITY:
        await updateItem(() => teamService.updateActivityUser(new UpdateActivityUserRequest(requestData.server, requestData.sessionId, requestData.data)), successMessage);
        break;
      case FPADataTypes.PROJECT:
        await updateItem(() => teamService.updateProjectUser(new UpdateProjectUserRequest(requestData.server, requestData.sessionId, requestData.data)), successMessage);
        break;
      case FPADataTypes.FOLDER:
        await updateItem(() => teamService.updateFolderUser(new UpdateFolderUserRequest(requestData.server, requestData.sessionId, requestData.data)), successMessage);
        break;
    }
  };

  const closeSearchDialog = async (reload: boolean) => {
    setIsOpenSearchContact(false);
    setSelectedRoleId(''); // Reset selected role
    if (reload) {
      setShowLoader(true);
      // Reload both users and roles to ensure data consistency
      await Promise.all([
        loadResponsibleUsers(),
        getActivityTypeRoles()
      ]);
      setShowLoader(false);
    }
  };

  const openSearchDialogForRole = (roleId: string) => {
    setSelectedRoleId(roleId);
    setIsOpenSearchContact(true);
  };

  const closeNewContactDialog = async (reload: boolean) => {
    if (reload) {
      setShowLoader(true);
      // Reload both users and roles to ensure data consistency
      await Promise.all([
        loadResponsibleUsers(),
        getActivityTypeRoles()
      ]);
      setShowLoader(false);
    }
  };

  if (showLoader || !rolesLoaded) {
    return (
      <Flex width="100%" justifyContent={'center'} marginTop={10}>
        <ProgressCircle aria-label="Loading…" isIndeterminate />
      </Flex>
    );
  } else {
    return (
      <Flex direction={'column'} gap={'size-150'} position={'relative'} width={'100%'}>
        {!groupedUsers ? (
          // Original functionality when no roles defined
          <>
            <Flex direction={'row'} alignItems={'start'} justifyContent={'start'}>
              <Content position={'relative'} UNSAFE_className={styles.heading_text}>
                {responsibleUsers?.[0]?.roleName || t('responsible_users_without_roles', { ns: 'layout_components' })}
              </Content>
              <Flex direction={'row'} alignItems={'center'} justifyContent={'center'} gap={'size-100'} UNSAFE_className={styles.icon_add_parent}>
                <Content>
                  <Link
                    isQuiet
                    onPress={e => {
                      setIsOpenSearchContact(true);
                    }}
                  >
                    <i className="bi bi-plus fs-5">
                      <View UNSAFE_className={styles.icon_add_text}>{t('add', { ns: 'layout_components' })}</View>
                    </i>
                  </Link>
                </Content>
              </Flex>
            </Flex>
            <Flex direction={'column'}>
              <Flex maxHeight={{ base: '1000px', L: '450px', M: '450px' }} width="100%" direction="column" UNSAFE_style={{ overflowX: 'auto' }}>
                {responsibleUsers?.map((user: any, index: number) => (
                  <div key={`user-${user.id}-${index}`}>
                    <ContactCard
                      contact={user}
                      avatarSrc="../../../../../../assets/images/avatar--desktop--light@2x.png"
                      selectedItem={selectedItem}
                      FPADataTypes={FPADataTypes}
                      onSaveDescription={onSaveDescription}
                      currentItem={current_Item}
                      updateItem={updateItem}
                      closeDialog={closeNewContactDialog}
                    />
                  </div>
                ))}
              </Flex>
            </Flex>
          </>
        ) : (
          // Role-based functionality when roles are defined
          <Flex direction={'column'}>
            <Flex maxHeight={{ base: '1000px', L: '450px', M: '450px' }} width="100%" direction="column" UNSAFE_style={{ overflowX: 'auto' }}>
              {Object.entries(groupedUsers).map(([accessRoleId, roleGroup]: [string, any]) => (
                <Flex key={accessRoleId} direction={'column'} marginBottom={'size-200'}>
                  {/* Role Header with Add button */}
                  <Flex direction={'row'} alignItems={'start'} justifyContent={'start'} marginBottom={'size-100'}>
                    <Content position={'relative'} UNSAFE_className={styles.heading_text}>
                      {roleGroup.roleInfo.accessRoleName || roleGroup.roleInfo.name || roleGroup.roleInfo.roleName || `Role ${accessRoleId}`}
                    </Content>
                    <Flex direction={'row'} alignItems={'center'} justifyContent={'center'} gap={'size-100'} UNSAFE_className={styles.icon_add_parent}>
                      <Content>
                        <Link
                          isQuiet
                          onPress={e => {
                            openSearchDialogForRole(accessRoleId);
                          }}
                        >
                          <i className="bi bi-plus fs-5">
                            <View UNSAFE_className={styles.icon_add_text}>{t('add', { ns: 'layout_components' })}</View>
                          </i>
                        </Link>
                      </Content>
                    </Flex>
                  </Flex>
                  
                  {/* Users in this role */}
                  <Flex direction={'column'}>
                    {roleGroup.users.map((user: any, index: number) => (
                      <ContactCard
                        key={`${accessRoleId}-${user.id}-${index}`}
                        contact={user}
                        avatarSrc="../../../../../../assets/images/avatar--desktop--light@2x.png"
                        selectedItem={selectedItem}
                        FPADataTypes={FPADataTypes}
                        onSaveDescription={onSaveDescription}
                        currentItem={current_Item}
                        updateItem={updateItem}
                        closeDialog={closeNewContactDialog}
                      />
                    ))}
                  </Flex>
                </Flex>
              ))}
            </Flex>
          </Flex>
        )}

        <DialogContainer
          isDismissable
          onDismiss={() => {
            setIsOpenSearchContact(false);
            setSelectedRoleId('');
          }}
        >
          {isOpenSearchContact && (
            <ResponsibleUsersSearch
              key={`${selectedItem?.id}-${selectedRoleId}`}
              selectedItem={selectedItem}
              currentItem={current_Item}
              responsibleUsers={responsibleUsers}
              FPADataTypes={FPADataTypes}
              updateItem={updateItem}
              closeDialog={closeSearchDialog}
              roleId={selectedRoleId}
            />
          )}
        </DialogContainer>
      </Flex>
    );
  }
}

export const ResponsibleUsersComponent = LayoutComponent(ResponsibleUsers);
